<!--
  Zeigt die Rückmeldungen der Country Manager zu einem Reisetermin
  Als erstes ein Banner mit dem Status
  darunter die Detailkarte mit Bestätigungen,
  Ablehnungsgrund und den Alternativterminen, aus denen ein neuer Reisetermin angelegt werden kann
-->
<template>
  <div v-if="bearbeitungen.length || istCmAngefragt || istManuellGesetzt" class="cm-rueckmeldung">
    <div class="cm-rueckmeldung__banner" :style="bannerStyle">
      <div class="cm-rueckmeldung__banner-zeile">
        <component
          :is="bannerDarstellung.icon"
          v-if="bannerDarstellung.icon"
          :size="16"
          class="flex-shrink-0"
        />
        <span class="cm-rueckmeldung__banner-text">
          <span
            v-for="(teil, i) in bannerTeile"
            :key="`teil-${i}`"
            :class="{ 'cm-rueckmeldung__stark': teil.stark }"
          >
            {{ teil.text }}
          </span>
        </span>
        <span v-if="bannerKuerzel.length" class="cm-rueckmeldung__banner-pills">
          <span
            v-for="kuerzel in bannerKuerzel"
            :key="`banner-${kuerzel}`"
            class="pill-badge pill-badge--small"
            :style="bannerPillStyle"
          >
            {{ kuerzel }}
          </span>
        </span>
      </div>
      <div v-if="zeigeBannerCmListe" class="cm-rueckmeldung__banner-cms">
        <span class="cm-rueckmeldung__label">Zuständige CMs</span>
        <div class="cm-rueckmeldung__banner-pills">
          <span
            v-for="cm in zustaendigeCmsMitStatus"
            :key="`cm-${cm.kuerzel}`"
            class="pill-badge pill-badge--small"
            :style="pillStyle(cm.status)"
          >
            {{ cm.kuerzel }} &bull; {{ statusDarstellung(cm.status).label }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="landRestriktionsHinweise.length"
      class="restriktionen-hinweis restriktionen-hinweis--land restriktionen-hinweis--liste mt-2"
    >
      <div class="restriktionen-hinweis__kopf">
        <span class="restriktionen-hinweis__icon"><Globe :size="16" /></span>
        <strong>Restriktionen des Landes</strong>
      </div>
      <RestriktionenZeitraumZeile
        v-for="gruppe in landRestriktionsHinweise"
        :key="gruppe.id"
        :type="gruppe.type"
        :zeitraum="gruppe.zeitraum"
        :zusatz="gruppe.detail"
      />
    </div>

    <div v-if="zeigeDetails" class="cm-rueckmeldung__card">
      <div
        v-for="eintrag in bestaetigteEintraege"
        :key="`block-${eintrag.id}`"
        class="cm-rueckmeldung__block"
      >
        <div v-if="mehrereCms" class="cm-rueckmeldung__block-header">
          <span class="pill-badge" :style="pillStyle(eintrag.status)">
            {{ eintrag.countryManagerKuerzel }} &bull; {{ statusDarstellung(eintrag.status).label }}
          </span>
          <span class="cm-rueckmeldung__block-datum">
            bestätigt am
            <span class="cm-rueckmeldung__stark">{{ formatDatum(eintrag.datum) }}</span>
          </span>
        </div>
        <div class="cm-rueckmeldung__row">
          <span class="cm-rueckmeldung__label">Programmänderung</span>
          <span class="cm-rueckmeldung__value">{{ jaNein(eintrag.programmaenderungen) }}</span>
        </div>
        <div class="cm-rueckmeldung__row">
          <span class="cm-rueckmeldung__label">Kontingentänderung</span>
          <span class="cm-rueckmeldung__value">{{ jaNein(eintrag.kontingentaenderungen) }}</span>
        </div>
        <div class="cm-rueckmeldung__row" v-if="eintrag.kommentar">
          <span class="cm-rueckmeldung__label">Kommentar</span>
          <span class="cm-rueckmeldung__value">{{ eintrag.kommentar || '–' }}</span>
        </div>
      </div>

      <template v-if="ablehnung">
        <div v-if="zeigeKartenCmListe" class="cm-rueckmeldung__row">
          <span class="cm-rueckmeldung__label">Zuständige CMs</span>
          <div class="cm-rueckmeldung__value cm-rueckmeldung__pills">
            <span
              v-for="cm in zustaendigeCmsMitStatus"
              :key="`karte-${cm.kuerzel}`"
              class="pill-badge pill-badge--small"
              :style="pillStyle(cm.status)"
            >
              {{ cm.kuerzel }} &bull; {{ statusDarstellung(cm.status).label }}
            </span>
          </div>
        </div>
        <div class="cm-rueckmeldung__row" v-if="ablehnung.kommentar">
          <span class="cm-rueckmeldung__label">Kommentar</span>
          <span class="cm-rueckmeldung__value">{{ ablehnung.kommentar || '–' }}</span>
        </div>
        <div v-if="alternativen.length" class="cm-rueckmeldung__row">
          <span class="cm-rueckmeldung__label cm-rueckmeldung__label--alternativen">Alternativen</span>
          <div class="cm-rueckmeldung__value cm-rueckmeldung__alternativen">
            <div
              v-for="alternative in alternativen"
              :key="`alt-${alternative.iso}`"
              class="cm-rueckmeldung__alternative"
            >
              <button
                type="button"
                class="cm-rueckmeldung__radio"
                :class="{
                  'cm-rueckmeldung__radio--gewaehlt': alternative.gewaehlt,
                  'cm-rueckmeldung__radio--gesperrt': !alternative.klickbar && !alternative.gewaehlt,
                }"
                :style="alternative.chipStyle"
                :disabled="!alternative.klickbar"
                @click="waehleAlternativtermin(alternative.iso)"
              >
                <span
                  class="cm-rueckmeldung__radio-kreis"
                  :class="{ 'cm-rueckmeldung__radio-kreis--aktiv': alternative.aktiv }"
                  :style="alternative.kreisStyle"
                />
                {{ alternative.label }}
              </button>
              <template v-if="alternative.existierenderTermin && !alternative.gewaehlt">
                <span class="cm-rueckmeldung__warnung">
                  <TriangleAlert :size="14" />
                  Termin existiert bereits
                </span>
                <ReiseterminStateBadge :state="alternative.existierenderTermin.state" small />
              </template>
              <!-- Beim gewählten Alternativtermin steht der Terminstatus als Badge hinter dem Chip -->
              <ReiseterminStateBadge
                v-if="alternative.existierenderTermin && alternative.gewaehlt"
                :state="alternative.existierenderTermin.state"
                small
              />
            </div>
            <div v-if="hasChanges" class="cm-rueckmeldung__auswahl-hinweis">
              <Info :size="14" class="cm-rueckmeldung__auswahl-hinweis-icon" />
              <span>
                Beim Speichern wird der ausgewählte Termin neu angelegt und im Status
                <ReiseterminStateBadge state="CmAngefragt" small />
                an den CM versendet.
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns';
import { Globe, Info, TriangleAlert } from 'lucide-vue';
import { pillColorStyleObject, grauPillColors, bannerBgColor } from '@/core/common/helpers/utils';
import {
  getExistierendeReisetermine,
  speichereAlternativtermin,
} from '@/core/produkte/reisetermine/services/country-manager-bearbeitungen.service';
import RestriktionenZeitraumZeile from '@/components/restriktionen/restriktionen-zeitraum-zeile.vue';
import ReiseterminStateBadge from '@/components/produkte/reisetermine/reisetermin-state-badge.vue';
import {
  groupRestriktionenByDay,
  mapKonfigurationToRestriktionen,
  formatZeitraumKompakt,
  toDayKey,
} from '@/components/restriktionen/restriktionen-calendar.helper.js';
import { getRestriktionskonfiguration } from '@/core/restriktionen/services/restriktionskonfiguration.service.js';
import { reiseToIsoCodes } from '@/core/common/iso3166Countries.js';
import {
  CM_STATUS,
  getCmStatus,
  getCmStatusDarstellung,
  getAblehnungsgrundLabel,
  REISETERMIN_STATE_ZU_CM_STATUS,
} from './cm-rueckmeldung.constants';

export default {
  name: 'SidebarTerminplanungCmRueckmeldung',
  components: { Globe, Info, TriangleAlert, RestriktionenZeitraumZeile, ReiseterminStateBadge },
  props: {
    bearbeitungen: { type: Array, default: () => [] },
    reisekuerzel: { type: String, default: null },
    reiseterminState: { type: String, default: null },
    cmAngefragtAm: { type: String, default: null },
    zustaendigeCms: { type: Array, default: () => [] },
    reise: { type: Object, default: null },
  },
  data() {
    return {
      ausgewaehlterAlternativtermin: null,
      existierendeReisetermine: [],
      landRestriktionen: [],
      ladeToken: 0,
    };
  },
  computed: {
    istCmAngefragt() {
      return this.reiseterminState === 'CmAngefragt';
    },
    // Der CM-Status wurde manuell gesetzt
    istManuellGesetzt() {
      if (this.reiseterminState === 'CmBestaetigt') {
        return !this.bearbeitungen.some(b => b.bestaetigt === true);
      }
      if (this.reiseterminState === 'CmAbgelehnt') {
        return !this.bearbeitungen.some(b => b.bestaetigt === false);
      }
      return false;
    },
    eintraege() {
      const esGibtEineAblehnung = this.bearbeitungen.some(b => b.bestaetigt === false);
      return this.bearbeitungen.map(bearbeitung => ({
        ...bearbeitung,
        status: getCmStatus(bearbeitung, esGibtEineAblehnung),
        // Der Anfragezeitpunkt steht im Anlagedatum, die Rückmeldung im Änderungsdatum
        datum: bearbeitung.bestaetigt === null ? bearbeitung.createdDateUtc : bearbeitung.updatedDateUtc,
      }));
    },
    mehrereCms() {
      return this.eintraege.length > 1;
    },
    einzelneBearbeitung() {
      return this.mehrereCms ? null : this.eintraege[0];
    },
    ablehnung() {
      return this.eintraege.find(eintrag => eintrag.status === CM_STATUS.ABGELEHNT) ?? null;
    },
    bestaetigteEintraege() {
      return this.eintraege.filter(eintrag => eintrag.status === CM_STATUS.BESTAETIGT);
    },
    bannerStatus() {
      if (this.istCmAngefragt) {
        return CM_STATUS.ANGEFRAGT;
      }
      if (this.istManuellGesetzt) {
        return REISETERMIN_STATE_ZU_CM_STATUS[this.reiseterminState];
      }
      if (this.ablehnung) {
        return CM_STATUS.ABGELEHNT;
      }
      if (
        this.zustaendigeCmsMitStatus.length &&
        this.zustaendigeCmsMitStatus.every(cm => cm.status === CM_STATUS.BESTAETIGT)
      ) {
        return CM_STATUS.BESTAETIGT;
      }
      return CM_STATUS.ANGEFRAGT;
    },
    bannerDarstellung() {
      return getCmStatusDarstellung(this.bannerStatus);
    },
    bannerStyle() {
      return {
        backgroundColor: bannerBgColor(this.bannerDarstellung.pill),
        borderColor: this.bannerDarstellung.pill.borderColor,
        color: this.bannerDarstellung.pill.color,
      };
    },
    bannerPillStyle() {
      return this.pillStyle(this.bannerStatus);
    },
    angefragtDatum() {
      if (this.istCmAngefragt) {
        return this.cmAngefragtAm;
      }
      return this.eintraege.find(eintrag => eintrag.status === CM_STATUS.ANGEFRAGT)?.datum ?? null;
    },
    // Die zuständigen CMs stehen in den Metadaten, ihr Status in der Bearbeitungstabelle
    // Wer nicht geantwortet hat, ist angefragt
    zustaendigeCmsMitStatus() {
      const offenerStatus = getCmStatus(null, !!this.ablehnung);
      const ausBearbeitungen = this.eintraege.map(eintrag => ({
        kuerzel: eintrag.countryManagerKuerzel,
        status: eintrag.status,
      }));
      const uebrige = this.zustaendigeCms
        .map(cm => cm.countryManagerKuerzel)
        .filter(kuerzel => kuerzel && !ausBearbeitungen.some(cm => cm.kuerzel === kuerzel))
        .map(kuerzel => ({ kuerzel, status: offenerStatus }));
      return [...ausBearbeitungen, ...uebrige];
    },
    mehrereZustaendigeCms() {
      return this.zustaendigeCmsMitStatus.length > 1;
    },
    // Ab zwei zuständigen CMs klappt der Banner auf die Übersichtszeile auf
    zeigeBannerCmListe() {
      return this.bannerStatus === CM_STATUS.ANGEFRAGT && this.mehrereZustaendigeCms;
    },
    // Bei einer Ablehnung stehen die zuständigen CMs in der Detailkarte statt im Banner
    zeigeKartenCmListe() {
      return !!this.ablehnung && this.mehrereZustaendigeCms;
    },
    bannerKuerzel() {
      if (this.zeigeBannerCmListe || this.zeigeKartenCmListe) {
        return [];
      }
      if (this.istCmAngefragt || this.istManuellGesetzt) {
        return this.zustaendigeCms.map(cm => cm.countryManagerKuerzel).filter(Boolean);
      }
      return this.einzelneBearbeitung ? [this.einzelneBearbeitung.countryManagerKuerzel] : [];
    },
    bannerTeile() {
      const verb = this.bannerDarstellung.verb;

      // Ohne CM-Antwort gibt es kein Datum
      if (this.istManuellGesetzt) {
        return [{ text: 'Manuell auf CM ' }, { text: verb, stark: true }, { text: ' gesetzt.' }];
      }

      if (this.bannerStatus === CM_STATUS.ABGELEHNT) {
        const grund = getAblehnungsgrundLabel(this.ablehnung.ablehnungsgrund);
        const anzahlAblehnungen = this.eintraege.filter(e => e.status === CM_STATUS.ABGELEHNT).length;
        const teile = this.mehrereZustaendigeCms
          ? [
              { text: 'Von ' },
              { text: String(anzahlAblehnungen), stark: true },
              { text: ` von ${this.zustaendigeCmsMitStatus.length} zuständigen CMs ` },
            ]
          : [{ text: 'Vom CM ' }];
        teile.push({ text: verb, stark: true });
        teile.push({ text: ' am ' });
        teile.push({ text: this.formatDatum(this.ablehnung.datum), stark: true });
        if (grund) {
          teile.push({ text: ` — ${grund}.` });
        }
        return teile;
      }

      if (this.bannerStatus === CM_STATUS.BESTAETIGT) {
        if (this.mehrereZustaendigeCms) {
          return [
            { text: 'Von allen ' },
            { text: String(this.zustaendigeCmsMitStatus.length), stark: true },
            { text: ' zuständigen CMs ' },
            { text: verb, stark: true },
          ];
        }
        return [
          { text: 'Vom CM ' },
          { text: verb, stark: true },
          { text: ' am ' },
          { text: this.formatDatum(this.einzelneBearbeitung.datum), stark: true },
        ];
      }

      // CM Status CmAngefragt und cmAngefragtAm ist null
      if (!this.angefragtDatum) {
        return [{ text: 'Beim CM ' }, { text: verb, stark: true }, { text: ' — Rückmeldung offen.' }];
      }

      return [
        { text: 'Beim CM ' },
        { text: verb, stark: true },
        { text: ' am ' },
        { text: this.formatDatum(this.angefragtDatum), stark: true },
        { text: ' — Rückmeldung offen.' },
      ];
    },
    // Die Alternativtermine kommen als Abreisedaten, weil daraus ein Reisetermin angelegt wird
    alternativTermineIso() {
      return this.ablehnung?.alternativTermineAbreisedatum ?? [];
    },
    // Der bereits gespeicherte Alternativtermin der Ablehnung
    gespeicherterAlternativtermin() {
      return this.ablehnung?.gewaehltesAbreisedatumAlternativtermin ?? null;
    },
    // Ist ein Alternativtermin gespeichert, steht die Auswahl fest
    auswahlGesperrt() {
      return !!this.gespeicherterAlternativtermin;
    },
    // Alternativtermine des ablehnenden CM, angereichert um bereits angelegte Termine und die Markierung
    alternativen() {
      const gewaehlterTag = this.auswahlGesperrt ? toDayKey(this.gespeicherterAlternativtermin) : null;
      return this.alternativTermineIso.map(iso => {
        const existierenderTermin = this.existierendeReisetermine.find(
          termin => this.formatDatum(termin.abreisedatum) === this.formatDatum(iso)
        );
        const gewaehlt = !!gewaehlterTag && toDayKey(iso) === gewaehlterTag;
        return {
          iso,
          label: this.formatDatum(iso),
          existierenderTermin,
          gewaehlt,
          aktiv: gewaehlt || this.ausgewaehlterAlternativtermin === iso,
          klickbar: !this.auswahlGesperrt && !existierenderTermin,
          ...this.chipDarstellung(gewaehlt, existierenderTermin?.state),
        };
      });
    },
    landRestriktionsHinweise() {
      const restriktionenByDay = groupRestriktionenByDay(this.landRestriktionen);
      const gruppen = new Map();
      this.alternativen.forEach(alternative => {
        (restriktionenByDay[toDayKey(alternative.iso)] || []).forEach(entry => gruppen.set(entry.id, entry));
      });
      return [...gruppen.values()].map(entry => {
        const laender = (entry.laender || []).join(', ');
        return {
          id: entry.id,
          type: entry.type,
          zeitraum: formatZeitraumKompakt(entry),
          detail: laender ? `${laender} · ${entry.grund}` : entry.grund,
        };
      });
    },
    // Die Karte erscheint nur, wenn mindestens ein Block Inhalt hat
    zeigeDetails() {
      const ablehnungMitInhalt =
        !!this.ablehnung &&
        (this.zeigeKartenCmListe || !!this.ablehnung.kommentar || this.alternativen.length > 0);
      return this.bestaetigteEintraege.length > 0 || ablehnungMitInhalt;
    },
    hasChanges() {
      return !this.auswahlGesperrt && !!this.ausgewaehlterAlternativtermin;
    },
  },
  watch: {
    // Die Alternativtermine hängen an der Ablehnung
    ablehnung: {
      handler() {
        this.ausgewaehlterAlternativtermin = null;
        // Veraltete Antworten beim schnellen Wechsel des Reisetermins verwerfen
        const token = ++this.ladeToken;
        this.ladeExistierendeReisetermine(token);
        this.ladeLandRestriktionen(token);
      },
      immediate: true,
    },
    hasChanges() {
      this.emitChanged();
    },
  },
  methods: {
    statusDarstellung: getCmStatusDarstellung,
    formatDatum(wert) {
      if (!wert) {
        return '–';
      }
      const datum = typeof wert === 'string' ? parseISO(wert) : new Date(wert);
      return format(datum, 'dd.MM.yyyy');
    },
    waehleAlternativtermin(iso) {
      this.ausgewaehlterAlternativtermin = this.ausgewaehlterAlternativtermin === iso ? null : iso;
    },
    // Der gespeicherte Alternativtermin wird in den Farben des daraus angelegten Reisetermins markiert
    chipDarstellung(gewaehlt, state) {
      if (!gewaehlt) {
        return { chipStyle: null, kreisStyle: null };
      }
      const cmStatus = REISETERMIN_STATE_ZU_CM_STATUS[state];
      const darstellung = cmStatus ? getCmStatusDarstellung(cmStatus) : null;
      const farben = darstellung?.pill ?? grauPillColors;
      return {
        chipStyle: {
          backgroundColor: darstellung ? bannerBgColor(darstellung.pill) : farben.background,
          borderColor: farben.borderColor,
        },
        kreisStyle: { borderColor: farben.borderColor, color: farben.borderColor },
      };
    },
    jaNein(wert) {
      if (wert === null || wert === undefined) {
        return '–';
      }
      return wert ? 'Ja' : 'Nein';
    },
    pillStyle(status) {
      return pillColorStyleObject(getCmStatusDarstellung(status).pill);
    },
    // Hier werden die Restriktionen der Reiseländer für den Zeitraum der Alternativtermine geladen
    // Ohne reisen-Parameter liefert der Endpunkt nur Ländergruppen, harte Reise-Sperren bleiben außen vor
    async ladeLandRestriktionen(token) {
      const termine = this.alternativTermineIso;
      const laender = reiseToIsoCodes(this.reise);
      if (!termine.length || !laender.length) {
        if (token === this.ladeToken) {
          this.landRestriktionen = [];
        }
        return;
      }
      const datumsWerte = termine.map(datum => new Date(datum)).sort((a, b) => a - b);
      const result = await getRestriktionskonfiguration({
        von: format(datumsWerte[0], 'yyyy-MM-dd'),
        bis: format(datumsWerte[datumsWerte.length - 1], 'yyyy-MM-dd'),
        laender,
      });
      if (token !== this.ladeToken) {
        return;
      }
      this.landRestriktionen = mapKonfigurationToRestriktionen(result);
    },
    async ladeExistierendeReisetermine(token) {
      const termine = this.alternativTermineIso;
      if (!termine.length || !this.reisekuerzel) {
        if (token === this.ladeToken) {
          this.existierendeReisetermine = [];
        }
        return;
      }
      const reisetermine = await getExistierendeReisetermine(this.reisekuerzel, termine);
      if (token !== this.ladeToken) {
        return;
      }
      this.existierendeReisetermine = reisetermine;
    },
    emitChanged() {
      this.$emit('changed', { hasChanges: this.hasChanges, isValid: true });
    },
    async save() {
      if (!this.hasChanges) {
        return;
      }
      await speichereAlternativtermin({
        bearbeitungId: this.ablehnung.id,
        abreisedatum: this.ausgewaehlterAlternativtermin,
      });
      this.ausgewaehlterAlternativtermin = null;
    },
  },
};
</script>

<style scoped>
.cm-rueckmeldung__banner {
  display: flex;
  flex-direction: column;
  padding: 10px 16px;
  border: 1px solid;
  border-radius: var(--radius-md);
  font-size: 14px;
  line-height: 20px;
}
.cm-rueckmeldung__banner-zeile {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cm-rueckmeldung__banner-cms {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--border);
}
.cm-rueckmeldung__banner-text {
  flex: 1;
  min-width: 0;
  color: var(--text-body);
}
.cm-rueckmeldung__banner-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.cm-rueckmeldung__stark {
  font-weight: 600;
}
.cm-rueckmeldung__card {
  margin-top: 8px;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background-color: var(--surface);
}
.cm-rueckmeldung__row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 4px 0;
}
.cm-rueckmeldung__label {
  flex: 0 0 168px;
  font-size: 12px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--text-body);
  margin-left: 24px;
}
.cm-rueckmeldung__value {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  line-height: 24px;
  color: var(--text);
  white-space: pre-line;
}
.cm-rueckmeldung__block + .cm-rueckmeldung__block {
  margin: 8px -16px 0;
  padding: 8px 16px 0;
  border-top: 1px solid var(--border);
}
.cm-rueckmeldung__block-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border);
}
.cm-rueckmeldung__block-datum {
  font-size: 14px;
  color: var(--text-body);
}
.cm-rueckmeldung__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.cm-rueckmeldung .pill-badge {
  height: 22px;
}
.cm-rueckmeldung__label--alternativen {
  line-height: 22px;
}
.cm-rueckmeldung__alternativen {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.cm-rueckmeldung__alternative {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cm-rueckmeldung__radio {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 22px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface);
  font-size: 12px;
  line-height: 14px;
  font-weight: 600;
  color: var(--text);
  cursor: pointer;
}
.cm-rueckmeldung__radio:disabled {
  cursor: default;
}
.cm-rueckmeldung__radio--gesperrt {
  color: var(--text-subtle);
  text-decoration: line-through;
}
.cm-rueckmeldung__radio-kreis {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  border: 1px solid var(--border);
  border-radius: 50%;
  background: transparent;
}
.cm-rueckmeldung__radio-kreis--aktiv {
  border-color: var(--primary);
  color: var(--primary);
}
.cm-rueckmeldung__radio-kreis--aktiv::after {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}
.cm-rueckmeldung__auswahl-hinweis {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 22px;
  color: var(--text-muted);
}
.cm-rueckmeldung__auswahl-hinweis-icon {
  flex-shrink: 0;
  color: var(--primary);
}
.cm-rueckmeldung__warnung {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--restriktion-keine-abreise-text);
}
</style>
